import { useState } from 'react';
import { TFunction } from 'react-i18next';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { useTranslation } from '../../../hooks/i18n';
import Typography from '../../Typography';
import TwoFactorAuthInput from '../../TwoFactorAuthInput';
import Button from '../../Button';
import { InfoCircleIcon } from '../../../components/Icons';
import { generateNewRecoveryCode } from '../../../services/auth';
import { twoFactorEvents } from '../../../constants/events';
import { COLORS } from '../../../themev2';

interface Step1ValidateTwoFAAndGenerateNewRecoveryCodeProps {
  t: TFunction;
  onGenerateNewRecoveryCode: (recoveryCode: string) => void;
}

export default function Step1ValidateTwoFAAndGenerateNewRecoveryCode({
  onGenerateNewRecoveryCode,
}: Step1ValidateTwoFAAndGenerateNewRecoveryCodeProps) {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    logEvent(twoFactorEvents.GENERATE_RECOVERY_CODE);
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError(t('common:twoFactor.invalidCodeError'));
      return;
    }
    logEvent(twoFactorEvents.TWO_FACTOR_ATTEMPT);

    try {
      setIsLoading(true);
      const response = await generateNewRecoveryCode(code);

      if (!response.recoveryCode) {
        throw new Error('Invalid 2FA code');
      }

      logEvent(twoFactorEvents.TWO_FACTOR_SUCCESS);
      logEvent(twoFactorEvents.GENERATE_RECOVERY_CODE_SUCCESS);
      onGenerateNewRecoveryCode(response.recoveryCode);
    } catch (error) {
      logEvent(twoFactorEvents.GENERATE_RECOVERY_CODE_ERROR, { Error: error });
      setError(t('common:twoFactor.verificationError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container rowSpacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
          {t('myAccount:security.generateRecoveryCodeModal.validateTwoFAAndGenerateCodeTitle')}
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'left', color: COLORS.gray[1100] }}>
          {t('myAccount:security.generateRecoveryCodeModal.validateTwoFAAndGenerateCodeDescription')}
        </Typography>
        <Divider sx={{ borderColor: COLORS.gray[900], marginTop: '1em' }} />
      </Grid>
      <Grid item xs={12}>
        <Grid
          sx={{
            display: 'flex',
            alignItems: 'start',
            gap: '4px',
            backgroundColor: '#E0E4F2',
            padding: '16px',
            borderRadius: '12px',
          }}
        >
          <InfoCircleIcon />
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: 600,
              color: COLORS.brand.night,
            }}
          >
            {t('myAccount:security.generateRecoveryCodeModal.validateTwoFAAndGenerateCodeDisclaimer')}
          </Typography>
        </Grid>
      </Grid>
      <TwoFactorAuthInput
        onSubmit={handleSubmit}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        error={error}
        onError={setError}
      />
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: { xs: '100%', lg: '80%' },
          }}
        >
          <Button
            fullWidth
            color="secondary"
            disabled={!verificationCode.every((digit) => digit !== '')}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {t('myAccount:security.generateRecoveryCodeModal.generateRecoveryCode')}
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
}
